import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Grid } from '@mui/material';
import type { LoginRequestPayload } from '@vy/api-definition';
import { LoginRequestSchema } from '@vy/api-definition';
import { Helmet } from 'react-helmet';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useVyNavigate } from '../../../hooks/use-vy-navigate';
import { login } from '../../../services/user-auth';
import loginBackground from '../../../assets/images/login-background.png';
import { LoginPageForm } from './login-page-components/LoginPageForm';
import { DescopeSignIn } from './login-page-components/DescopeSignIn';
import { defined } from '@vy/null';
import { useState } from 'react';

export const LoginPage: React.FC = () => {
  const navigate = useVyNavigate();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const [descopeCredentials, setDescopeCredentials] = useState<{ email: string; password: string } | null>(null);

  const methods = useForm<LoginRequestPayload>({
    resolver: zodResolver(LoginRequestSchema),
  });

  const loginMutation = useMutation(login, {
    onSuccess: () => {
      const returnTo = searchParams.get('returnTo');

      const returnToExists = defined(returnTo);
      const isRelativePath = returnTo?.startsWith('/');
      const isProtocolRelative = returnTo?.startsWith('//');

      if (returnToExists && isRelativePath && !isProtocolRelative) {
        window.location.href = returnTo;

        return;
      }

      const path = state?.from?.pathname ?? '/';
      navigate(path, { replace: true });
    },
  });

  const handleSubmit = methods.handleSubmit(async (data) => {
    setDescopeCredentials({ email: data.username, password: data.password });

    await loginMutation.mutateAsync(data);
  });

  return (
    <>
      <Helmet>
        <title>Login</title>
      </Helmet>
      <DescopeSignIn credentials={descopeCredentials} />
      <Grid container style={{ height: '100vh', overflow: 'hidden' }}>
        <Grid
          size={{
            lg: 5
          }}
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit}>
              <LoginPageForm />
            </form>
          </FormProvider>
        </Grid>

        <Grid
          size={{
            lg: 7
          }}
          sx={{
            display: { xs: 'none', lg: 'flex' },
            background: '#E7F2FF',
            alignItems: 'center',
            justifyContent: 'flex-end',
            overflow: 'hidden',
            pl: '7%',
            position: 'relative'
          }}>
          <Box
            component="img"
            src={loginBackground}
            alt="Vayu dashboard preview"
            sx={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: '100%',
              width: '100%',
              height: 'auto',
              borderRadius: '24px 0 0 24px',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: '75%',
              height: '60px',
              opacity: 0.8,
              background: 'linear-gradient(to bottom, rgba(255,255,255,0), white)',
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};
